<template>
  <div class="DashboardOnboarding">
    <div class="DashboardOnboarding__header">
      <h3 class="DashboardOnboarding__title">
        {{ isPaidUser ? 'Explore Forge Features' : 'Complete Your Trial Setup' }}
      </h3>
      <p class="DashboardOnboarding__subtitle">
        {{ isPaidUser 
          ? 'Continue exploring Forge\'s powerful features to get the most out of your subscription.'
          : 'Get the most out of your trial by completing these essential tasks.'
        }}
      </p>
    </div>

    <div class="DashboardOnboarding__progress-bar">
      <div 
        class="DashboardOnboarding__progress-fill"
        :style="{ width: `${progressPercentage}%` }"
      ></div>
    </div>
    
    <div class="DashboardOnboarding__progress-header">
      <div class="DashboardOnboarding__progress-text">
        {{ completedTasks }}/{{ totalTasks }} completed ({{ Math.round(progressPercentage) }}%)
      </div>
      <v-btn
        v-if="nextTask"
        @click="handleNextTaskClick"
        class="DashboardOnboarding__next-btn"
        small
        :color="nextTask.actionUrl ? 'orange' : 'grey'"
        :disabled="!nextTask"
      >
        <v-icon left small>
          {{ nextTask.actionUrl ? 'mdi-arrow-right' : 'mdi-arrow-down' }}
        </v-icon>
        {{ nextTask.actionUrl ? 'Start Next' : 'View Next' }}
      </v-btn>
    </div>

    <div class="DashboardOnboarding__tasks-container">
      <!-- Loading State -->
      <div v-if="loading" class="DashboardOnboarding__loading">
        <div class="DashboardOnboarding__loading-spinner">
          <v-progress-circular
            indeterminate
            color="#F59E0B"
            size="32"
          />
        </div>
        <p class="DashboardOnboarding__loading-text">Loading your progress...</p>
      </div>
      
      <!-- Error State -->
      <div v-else-if="error" class="DashboardOnboarding__error">
        <v-icon color="#EF4444" class="DashboardOnboarding__error-icon">mdi-alert-circle</v-icon>
        <p class="DashboardOnboarding__error-text">{{ error }}</p>
        <v-btn 
          @click="loadProgress" 
          class="DashboardOnboarding__retry-btn"
          small
          color="orange"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Retry
        </v-btn>
      </div>
      
      <!-- Tasks List -->
      <div v-else class="DashboardOnboarding__tasks">
        <div 
          v-for="task in tasks" 
          :key="task.id"
          class="DashboardOnboarding__task"
          :class="{ 
            'DashboardOnboarding__task--completed': task.completed,
            'DashboardOnboarding__task--clickable': task.actionUrl 
          }"
          @click="handleTaskClick(task)"
        >
          <div class="DashboardOnboarding__task-checkbox">
            <v-icon 
              v-if="task.completed" 
              color="#6bc77b" 
              class="DashboardOnboarding__check-icon"
            >
              mdi-check-circle
            </v-icon>
            <div 
              v-else 
              class="DashboardOnboarding__unchecked-circle"
            ></div>
          </div>
          
          <div class="DashboardOnboarding__task-content">
            <h4 class="DashboardOnboarding__task-title">{{ task.title }}</h4>
            <p class="DashboardOnboarding__task-description">{{ task.description }}</p>
          </div>
          
          <div 
            v-if="task.actionUrl" 
            class="DashboardOnboarding__task-action"
          >
            <v-icon class="DashboardOnboarding__arrow-icon">mdi-chevron-right</v-icon>
          </div>
        </div>
      </div>
    </div>

    <div class="DashboardOnboarding__footer">
      <p class="DashboardOnboarding__help-text">
        Need help? Check out our 
        <a href="https://docs.getforge.com" target="_blank" class="DashboardOnboarding__help-link">
          documentation
        </a> 
        or contact our support team.
      </p>
    </div>

    <!-- Achievement Badge Animation -->
    <AchievementBadge
      v-if="shouldShowBadgeAnimation"
      :show-animation="true"
      :auto-start="true"
      badge-title="Striker!"
      badge-description="Congratulations! You've earned your place at the forge. As a Striker, you're ready to swing the hammer and begin your smithing journey."
      @animation-complete="handleBadgeAnimationComplete"
      @animation-hidden="handleBadgeAnimationHidden"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import AchievementBadge from '@/components/_Common/AchievementBadge.vue'

export default {
  name: 'DashboardOnboarding',
  components: {
    AchievementBadge
  },
  async mounted() {
    // Load onboarding progress from API
    try {
      await this.loadProgress()
    } catch (error) {
      console.error('Failed to load onboarding progress:', error)
      // Component will show loading state or error from store
    }
  },
  computed: {
    ...mapState('user', {
      user: 'current'
    }),
    ...mapState('onboarding', {
      loading: 'loading',
      error: 'error',
      lastUpdated: 'lastUpdated'
    }),
    ...mapGetters('onboarding', {
      progressPercentage: 'completionPercentage',
      completedTasks: 'completedCount',
      totalTasks: 'totalCount',
      tasks: 'progressList',
      nextIncompleteTask: 'nextIncompleteTask',
      isCompleted: 'isCompleted',
      shouldShowBadgeAnimation: 'shouldShowBadgeAnimation',
      hasBadge: 'hasBadge'
    }),
    
    isPaidUser() {
      return this.user?.isPaid || false
    },
    
    nextTask() {
      return this.nextIncompleteTask
    },
  },
  
  methods: {
    ...mapActions('onboarding', ['loadProgress', 'markBadgeAsShown', 'triggerAvatarNotification']),
    handleTaskClick(task) {
      if (task.actionUrl) {
        if (task.actionUrl.startsWith('http')) {
          // External link
          window.open(task.actionUrl, '_blank')
        } else {
          // Internal route
          this.$router.push(task.actionUrl)
        }
      }
    },
    
    handleNextTaskClick() {
      if (!this.nextTask) return
      
      // If the task has an action URL, go there directly
      if (this.nextTask.actionUrl) {
        this.handleTaskClick(this.nextTask)
        return
      }
      
      // Otherwise, scroll to the task
      this.scrollToNextTask()
    },
    
    scrollToNextTask() {
      if (!this.nextTask) return
      
      // Find the next incomplete task element
      const nextTaskIndex = this.tasks.findIndex(task => task.id === this.nextTask.id)
      const taskElements = this.$el.querySelectorAll('.DashboardOnboarding__task')
      
      if (taskElements[nextTaskIndex]) {
        const tasksContainer = this.$el.querySelector('.DashboardOnboarding__tasks-container')
        const taskElement = taskElements[nextTaskIndex]
        
        // Scroll the task into view within the container
        tasksContainer.scrollTo({
          top: taskElement.offsetTop - tasksContainer.offsetTop - 20,
          behavior: 'smooth'
        })
        
        // Add a subtle highlight effect
        taskElement.classList.add('DashboardOnboarding__task--highlight')
        setTimeout(() => {
          taskElement.classList.remove('DashboardOnboarding__task--highlight')
        }, 2000)
      }
    },
    
    handleBadgeAnimationComplete() {
      // Mark the badge animation as shown so it won't show again
      this.markBadgeAsShown()
      
      // Trigger the avatar notification after a short delay
      setTimeout(() => {
        this.triggerAvatarNotification()
      }, 1000) // 1 second delay to let the user notice the badge animation completed
    },
    
    handleBadgeAnimationHidden() {
      // Animation is now hidden, could trigger additional actions if needed
    }
  },
  
  watch: {
    // Watch for when onboarding becomes complete to trigger badge animation
    shouldShowBadgeAnimation(newVal) {
      if (newVal) {
        // Small delay to ensure the component is ready
        this.$nextTick(() => {
          // The AchievementBadge component will auto-start if shouldShowBadgeAnimation is true
        })
      }
    }
  },
}
</script>

<style lang="scss">
$style: DashboardOnboarding;

.#{$style} {
  @extend %section-styles;
  height: 100%;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  padding: 20px;
  
  &__header {
    margin-bottom: 16px;
    flex-shrink: 0;
  }
  
  &__title {
    font-size: $H18;
    font-weight: 600;
    color: $white;
    margin: 0 0 6px 0;
  }
  
  &__subtitle {
    font-size: $H12;
    color: $N2;
    margin: 0;
    line-height: 1.4;
  }
  
  &__progress-bar {
    height: 6px;
    background-color: $N5;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 6px;
    flex-shrink: 0;
  }
  
  &__progress-fill {
    height: 100%;
    background: linear-gradient(90deg, $G1, lighten($G1, 10%));
    border-radius: 3px;
    transition: width 0.3s ease;
  }
  
  &__progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-shrink: 0;
  }
  
  &__progress-text {
    font-size: $H10;
    color: $N2;
  }
  
  &__next-btn {
    .v-btn {
      text-transform: none !important;
      font-size: $H10 !important;
      font-weight: 500;
      height: 28px !important;
      min-width: auto !important;
      padding: 0 12px !important;
      
      &.theme--dark.v-btn.orange {
        background-color: $Y1 !important;
        color: $white !important;
        
        &:hover {
          background-color: lighten($Y1, 10%) !important;
        }
      }
      
      .v-icon {
        font-size: 16px !important;
      }
    }
  }
  
  &__tasks-container {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 12px;
    padding: 4px; // Add padding to prevent clipping of highlight effect
    margin: -4px; // Offset the padding to maintain original size
    margin-bottom: 8px;
    
    // Custom scrollbar styling for dark theme
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: $N5;
      border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: $N3;
      border-radius: 2px;
      
      &:hover {
        background: $N2;
      }
    }
  }
  
  &__loading,
  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    text-align: center;
    height: 200px;
  }
  
  &__loading-spinner {
    margin-bottom: 16px;
  }
  
  &__loading-text {
    color: $N2;
    font-size: $H12;
    margin: 0;
  }
  
  &__error-icon {
    margin-bottom: 12px;
    font-size: 24px !important;
  }
  
  &__error-text {
    color: $N2;
    font-size: $H12;
    margin: 0 0 16px 0;
    max-width: 250px;
    line-height: 1.4;
  }
  
  &__retry-btn {
    .v-btn {
      text-transform: none !important;
      font-size: $H11 !important;
      font-weight: 500;
      
      &.theme--dark.v-btn.orange {
        background-color: $Y1 !important;
        color: $white !important;
        
        &:hover {
          background-color: lighten($Y1, 10%) !important;
        }
      }
    }
  }
  
  &__tasks {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  &__task {
    display: flex;
    align-items: flex-start;
    padding: 12px;
    border: 1px solid $N5;
    border-radius: 6px;
    transition: all 0.2s ease;
    background: rgba($N5, 0.3);
    
    &--completed {
      background-color: rgba($G1, 0.1);
      border-color: rgba($G1, 0.3);
    }
    
    &--clickable {
      cursor: pointer;
      
      &:hover {
        border-color: $Y1;
        background: rgba($Y1, 0.1);
        box-shadow: 0 2px 8px rgba($Y1, 0.2);
      }
    }
    
    &--highlight {
      border-color: $Y1 !important;
      background: rgba($Y1, 0.15) !important;
      box-shadow: 0 0 12px rgba($Y1, 0.3) !important;
      transform: scale(1.01);
      transition: all 0.3s ease;
    }
  }
  
  &__task-checkbox {
    margin-right: 10px;
    margin-top: 1px;
    flex-shrink: 0;
  }
  
  &__check-icon {
    font-size: 18px !important;
  }
  
  &__unchecked-circle {
    width: 18px;
    height: 18px;
    border: 2px solid $N3;
    border-radius: 50%;
  }
  
  &__task-content {
    flex: 1;
    min-width: 0; // Allows text to wrap properly
  }
  
  &__task-title {
    font-size: $H13;
    font-weight: 500;
    color: $white;
    margin: 0 0 3px 0;
    line-height: 1.3;
  }
  
  &__task-description {
    font-size: $H11;
    color: $N2;
    margin: 0;
    line-height: 1.3;
  }
  
  &__task-action {
    margin-left: 8px;
    color: $N2;
    flex-shrink: 0;
    
    .#{$style}__task--clickable:hover & {
      color: $Y1;
    }
  }
  
  &__arrow-icon {
    font-size: 16px !important;
  }
  
  &__footer {
    border-top: 1px solid $N5;
    padding-top: 12px;
    flex-shrink: 0;
  }
  
  &__help-text {
    font-size: $H10;
    color: $N2;
    margin: 0;
    text-align: center;
    line-height: 1.3;
  }
  
  &__help-link {
    color: $Y1;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
